import React, { useState } from 'react';
import './HistoriqueSN.css';

// === FONCTIONS UTILITAIRES POUR LE TRAITEMENT DES PANNES ===
const cleanStepName = (name) => {
  if (!name || typeof name !== 'string') return "";
  return name.toLowerCase().trim().replace(/\s+/g, "").replace(/[_-]/g, "");
};

const normalizePanneName = (panneName) => {
  if (!panneName || typeof panneName !== 'string') return panneName;
  
  // Mapping pour normaliser les noms
  const map = {
    leftStickX: 'leftStick',
    leftStickY: 'leftStick',
    rightStickX: 'rightStick',
    rightStickY: 'rightStick',
    leftStickXmin: 'leftStick',
    leftStickXmax: 'leftStick',
    leftStickYmin: 'leftStick',
    leftStickYmax: 'leftStick',
    rightStickXmin: 'rightStick',
    rightStickXmax: 'rightStick',
    rightStickYmin: 'rightStick',
    rightStickYmax: 'rightStick',
    playStation: 'psButton',
    dynamicJoystickOK: 'dynamicJoystick',
    noSpecialCaseErrorFound: 'specialCase',
    micAudioCheck: 'microphone',
    bluetooth: 'bluetooth'
  };

  return map[panneName] || panneName;
};

// FONCTION PRINCIPALE : Extraire UNE SEULE panne du failureStep
const getRealPanneFromSession = (session) => {
  if (!session) return 'Inconnu';
  
  // Si globalStatus est OK, retourner PASS
  if (session.globalStatus === 'OK') {
    return 'PASS';
  }
  
  // Pour les sessions KO, analyser le testTracking
  if (!session.testTracking) {
    return 'NO_DATA';
  }
  
  try {
    const testData = typeof session.testTracking === 'string' 
      ? JSON.parse(session.testTracking) 
      : session.testTracking;
    
    if (!testData || typeof testData !== 'object') {
      return 'INVALID_DATA';
    }
    
    const failureStep = testData.failureStep;
    if (!failureStep) {
      return 'NO_FAILURE_STEP';
    }
    
    let stepData = testData[failureStep];
    
    if (!stepData || typeof stepData !== 'object') {
      const cleanStep = cleanStepName(failureStep);
      for (const key in testData) {
        if (cleanStepName(key) === cleanStep && testData[key] && typeof testData[key] === 'object') {
          stepData = testData[key];
          break;
        }
      }
    }
    
    if (!stepData || typeof stepData !== 'object') {
      return 'STEP_DATA_MISSING';
    }
    
    const cleanFailureStep = cleanStepName(failureStep);
    
    if (cleanFailureStep === 'stepone') {
      // Step one : true => FAIL
      for (const key in stepData) {
        if (stepData[key] === true) {
          return normalizePanneName(key);
        }
      }
    } else {
      // Step two à seven : null ou false => FAIL
      // D'abord chercher les NULL
      for (const key in stepData) {
        if (stepData[key] === null) {
          return normalizePanneName(key);
        }
      }
      
      // Puis chercher les FALSE
      for (const key in stepData) {
        if (stepData[key] === false) {
          return normalizePanneName(key);
        }
      }
    }
    
    return 'NO_FAILURE_DETECTED';
    
  } catch (error) {
    console.error("❌ Erreur extraction panne:", error, session.realSerialNumber);
    return 'EXTRACTION_ERROR';
  }
};

// FONCTION CORRIGÉE : Extraire TOUTES les pannes d'un failureStep (spécialement pour "Other")
const extractPannesFromFailureStepForOther = (session) => {
  if (!session || session.globalStatus !== 'KO' || session.symptome_description !== 'Other') {
    return [];
  }
  
  if (!session.testTracking) {
    return [];
  }
  
  try {
    const testData = typeof session.testTracking === 'string' 
      ? JSON.parse(session.testTracking) 
      : session.testTracking;
    
    if (!testData || typeof testData !== 'object') {
      return [];
    }
    
    const failureStep = testData.failureStep;
    if (!failureStep) {
      return [];
    }
    
    let stepData = testData[failureStep];
    
    // Si stepData n'existe pas directement, chercher par nom nettoyé
    if (!stepData || typeof stepData !== 'object') {
      const cleanStep = cleanStepName(failureStep);
      for (const key in testData) {
        if (cleanStepName(key) === cleanStep && testData[key] && typeof testData[key] === 'object') {
          stepData = testData[key];
          break;
        }
      }
    }
    
    if (!stepData || typeof stepData !== 'object') {
      return [];
    }
    
    const pannes = [];
    const cleanFailureStep = cleanStepName(failureStep);
    
    // ANALYSER TOUTES LES PANNES DANS L'ÉTAPE - NE PAS S'ARRÊTER À LA PREMIÈRE
    if (cleanFailureStep === 'stepone') {
      // Step one : true => FAIL (tous les true)
      for (const key in stepData) {
        if (stepData[key] === true) {
          const panne = normalizePanneName(key);
          if (!pannes.includes(panne)) {
            pannes.push(panne);
          }
        }
      }
    } else if (cleanFailureStep === 'steptwo') {
      // Step two : leftStick, rightStick, psButton
      for (const key in stepData) {
        if (stepData[key] === null || stepData[key] === false) {
          const panne = normalizePanneName(key);
          if (!pannes.includes(panne)) {
            pannes.push(panne);
          }
        }
      }
    } else if (cleanFailureStep === 'stepthree') {
      // Step three : leftStick, rightStick, dynamicJoystick
      for (const key in stepData) {
        if (stepData[key] === null || stepData[key] === false) {
          const panne = normalizePanneName(key);
          if (!pannes.includes(panne)) {
            pannes.push(panne);
          }
        }
      }
    } else if (cleanFailureStep === 'stepfour') {
      // Step four : buttons (a, b, x, y, up, down, left, right, l1, l2, r1, r2)
      for (const key in stepData) {
        if (stepData[key] === null || stepData[key] === false) {
          const panne = normalizePanneName(key);
          // Pour les boutons, garder le nom original
          if (!pannes.includes(panne)) {
            pannes.push(key); // Garder le nom original du bouton
          }
        }
      }
    } else if (cleanFailureStep === 'stepfive') {
      // Step five : Special case
      if (stepData.noSpecialCaseErrorFound !== true) {
        pannes.push('specialCase');
      }
      // Vérifier aussi les autres champs
      for (const key in stepData) {
        if (stepData[key] === null || stepData[key] === false) {
          if (key !== 'noSpecialCaseErrorFound') {
            const panne = normalizePanneName(key);
            if (!pannes.includes(panne)) {
              pannes.push(panne);
            }
          }
        }
      }
    } else if (cleanFailureStep === 'stepsix') {
      // Step six : Microphone
      if (stepData.micAudioCheck !== true) {
        pannes.push('microphone');
      }
      // Vérifier aussi les autres champs
      for (const key in stepData) {
        if (stepData[key] === null || stepData[key] === false) {
          if (key !== 'micAudioCheck') {
            const panne = normalizePanneName(key);
            if (!pannes.includes(panne)) {
              pannes.push(panne);
            }
          }
        }
      }
    } else if (cleanFailureStep === 'stepseven') {
      // Step seven : Bluetooth
      if (stepData.bluetooth !== true) {
        pannes.push('bluetooth');
      }
      // Vérifier aussi les autres champs
      for (const key in stepData) {
        if (stepData[key] === null || stepData[key] === false) {
          if (key !== 'bluetooth') {
            const panne = normalizePanneName(key);
            if (!pannes.includes(panne)) {
              pannes.push(panne);
            }
          }
        }
      }
    } else {
      // Pour les autres étapes non spécifiques : null ou false => FAIL
      // Analyser TOUS les champs
      for (const key in stepData) {
        if (stepData[key] === null || stepData[key] === false) {
          const panne = normalizePanneName(key);
          if (!pannes.includes(panne)) {
            pannes.push(panne);
          }
        }
      }
    }
    
    // Si aucune panne détectée mais que c'est une session KO
    if (pannes.length === 0 && session.globalStatus === 'KO') {
      return ['NO_PANNE_DETECTED_IN_STEP'];
    }
    
    return pannes;
    
  } catch (error) {
    console.error("❌ Erreur extraction pannes failureStep:", error, session.realSerialNumber);
    return ['EXTRACTION_ERROR'];
  }
};

// Fonction pour obtenir les pannes "Other" formatées pour l'affichage
const getOtherPannesFormatted = (session) => {
  if (!session || session.globalStatus !== 'KO' || session.symptome_description !== 'Other') {
    return 'N/A';
  }
  
  const pannes = extractPannesFromFailureStepForOther(session);
  
  if (pannes.length === 0) {
    return 'NO_PANNE_IN_FAILURE_STEP';
  }
  
  return pannes.join(', ');
};

// Fonction pour extraire le nom du failureStep
const getFailureStepName = (session) => {
  if (!session || !session.testTracking) {
    return 'N/A';
  }
  
  try {
    const testData = typeof session.testTracking === 'string' 
      ? JSON.parse(session.testTracking) 
      : session.testTracking;
    
    if (!testData || typeof testData !== 'object') {
      return 'N/A';
    }
    
    return testData.failureStep || 'N/A';
  } catch (error) {
    console.error("Erreur parsing testTracking:", error);
    return 'ERROR';
  }
};

// Fonction pour formater la panne détectée
const formatDetectedPanne = (panne) => {
  if (!panne) return 'N/A';
  
  const map = {
    'PASS': 'PASS',
    'NO_DATA': 'Pas de données test',
    'INVALID_DATA': 'Données test invalides',
    'NO_FAILURE_DETECTED': 'Pas de panne détectée',
    'EXTRACTION_ERROR': 'Erreur extraction',
    'NO_FAILURE_STEP': 'Pas de failure step',
    'STEP_DATA_MISSING': 'Données étape manquantes',
    'Inconnu': 'Inconnu',
    'NO_TESTTRACKING_FOR_DESIGNATED': 'N/A',
    'NO_PANNE_IN_FAILURE_STEP': 'Pas de panne dans failure step'
  };
  
  return map[panne] || panne;
};

// ============================================
// COMPOSANT PRINCIPAL HistoriqueSN
// ============================================

const HistoriqueSN = () => {
  const [sn, setSn] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('sessions');

  const searchAll = async () => {
    if (!sn.trim()) {
      setError('Veuillez entrer un numéro de série');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const encodedSN = encodeURIComponent(sn.trim());
      const baseUrl = "https://ds5testerapp.com/api";
       //const baseUrl = "http://10.10.9.59:4000";

      const [historyRes, quickRes, sessionRes] = await Promise.all([
        fetch(`${baseUrl}/searchSNhistory?serial_number=${encodedSN}`),
        fetch(`${baseUrl}/searchSNquick?serial_number=${encodedSN}`),
        fetch(`${baseUrl}/getAllSessionBySn?serial_number=${encodedSN}`)
      ]);

      const history = historyRes.ok ? await historyRes.json() : [];
      const quick = quickRes.ok ? await quickRes.json() : [];
      const sessions = sessionRes.ok ? await sessionRes.json() : [];

      setResults({ sessions, quick, history });
      
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchAll();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusClass = (status) => {
    if (status === 'OK' || status === 'PASS') return 'status-ok';
    if (status === 'KO' || status === 'FAIL') return 'status-ko';
    return '';
  };

  const formatOperator = (operatorId) => {
    if (!operatorId || operatorId === "null" || operatorId === "undefined" || 
        typeof operatorId !== 'string' || operatorId.trim() === "") {
      return "n/a";
    }
    return operatorId.trim();
  };

  const renderTable = (data, type) => {
    if (data.length === 0) {
      return <div className="no-data">Aucune donnée trouvée</div>;
    }

    if (type === 'sessions') {
      // Table sessions - avec traitement avancé des pannes
      return (
        <table>
          <thead>
            <tr>
              <th>Date de début</th>
              <th>Date de traitement</th>
              <th>SN Réel</th>
              <th>Testeur</th>
              <th>Opérateur</th>
              <th>Résultat</th>
              <th>Grade</th>
              <th>Panne déclarée</th>
              <th>Panne désignée (panne_descriptions)</th>
              <th>Panne détectée (testTracking)</th>
              <th>Pannes "Other" détectées</th>
              <th>Failure Step</th>
              <th>Famille de pannes</th>
            </tr>
          </thead>
          <tbody>
            {data.map((s, i) => {
              // Extraire la panne détectée
              const detectedPanne = getRealPanneFromSession(s);
              const formattedDetectedPanne = formatDetectedPanne(detectedPanne);
              
              // Déterminer le résultat
              const result = detectedPanne === 'PASS' ? 'PASS' : 'FAIL';
              
              // Extraire les pannes désignées
              const pannesDesignees = s.panne_descriptions ? 
                s.panne_descriptions.split(' , ').filter(d => d.trim() !== '') : [];
              
              // Extraire les pannes "Other" si applicable
              const otherPannes = s.symptome_description === 'Other' ? 
                getOtherPannesFormatted(s) : 'N/A';
              
              // Extraire le nom du failureStep
              const failureStep = getFailureStepName(s);
              
              return (
                <tr key={i}>
                  <td>{formatDate(s.startDate)}</td>
                  <td>{formatDate(s.processed_date)}</td>
                  <td className="serial">{s.realSerialNumber || s.deviceSerialNumber || '-'}</td>
                  <td>{s.raspberry_id || s.raspberry_serial || '-'}</td>
                  <td>{formatOperator(s.operator_name || s.operator)}</td>
                  <td>
                    <span className={`status ${getStatusClass(result)}`}>
                      {result}
                    </span>
                  </td>
                  <td>{s.grade || '-'}</td>
                  <td>{s.symptome_description || '-'}</td>
                  <td className="truncate" title={pannesDesignees.join(', ')}>
                    {pannesDesignees.length > 0 ? pannesDesignees.join(', ') : '-'}
                  </td>
                  <td className={detectedPanne !== 'PASS' && detectedPanne !== 'N/A' ? 'detected-panne' : ''}>
                    {formattedDetectedPanne}
                  </td>
                  <td className="other-pannes">
                    {otherPannes}
                  </td>
                  <td>{failureStep}</td>
                  <td>{s.famille_pannes || '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    if (type === 'quick') {
      // Table DriftQuicksession avec les colonnes spécifiques
      return (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Numéro de série</th>
              <th>État</th>
              <th>Quick Drift</th>
              <th>Calibration</th>
              <th>Raison</th>
              <th>Touchpad</th>
              <th>Modèle</th>
              <th>Version</th>
            </tr>
          </thead>
          <tbody>
            {data.map((q, i) => (
              <tr key={i}>
                <td>{formatDate(q.date)}</td>
                <td className="serial">{q.serial_number}</td>
                <td>
                  <span className={`status ${getStatusClass(q.state)}`}>
                    {q.state || '-'}
                  </span>
                </td>
                <td>{q.quickdriftname || '-'}</td>
                <td>{q.calibration || '-'}</td>
                <td>{q.Reason || '-'}</td>
                <td>{q.Touchpad || '-'}</td>
                <td>{q.Model || '-'}</td>
                <td>{q.version || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (type === 'history') {
      // Table ds5_history avec les colonnes spécifiques
      // MAINTENANT avec les noms des mouvements au lieu des IDs
      return (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Numéro de série</th>
              <th>Opérateur</th>
              <th>Testeur</th>
              <th>Grade</th>
              <th>Panne</th>
              <th>Mouvement actuel</th>
              <th>Prochain mouvement</th>
              <th>Version</th>
              <th>Cause rebut</th>
              <th>Panne réparée</th>
            </tr>
          </thead>
          <tbody>
            {data.map((h, i) => (
              <tr key={i}>
                <td>{formatDate(h.date)}</td>
                <td className="serial">{h.serial_number}</td>
                <td>{h.operator || '-'}</td>
                <td>{h.tester || '-'}</td>
                <td>{h.Grade || '-'}</td>
                <td>{h.Panne || '-'}</td>
                <td>
                  <span className={`movement ${h.current_movement ? 'active' : ''}`}>
                    {h.current_movement || '-'}
                  </span>
                </td>
                <td>
                  <span className={`movement ${h.next_movement ? 'next' : ''}`}>
                    {h.next_movement || '-'}
                  </span>
                </td>
                <td>{h.version || '-'}</td>
                <td className="truncate" title={h.cause_rebut}>{h.cause_rebut || '-'}</td>
                <td className="truncate" title={h.repaired_panne}>{h.repaired_panne || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
  };

  // Calcul des statistiques pour le header
  const calculateStats = () => {
    if (!results || !results.sessions || results.sessions.length === 0) {
      return {
        total: 0,
        pass: 0,
        fail: 0,
        passRate: 0,
        otherCount: 0
      };
    }

    const sessions = results.sessions;
    const total = sessions.length;
    let pass = 0;
    let fail = 0;
    let otherCount = 0;

    sessions.forEach(session => {
      const detectedPanne = getRealPanneFromSession(session);
      if (detectedPanne === 'PASS') {
        pass++;
      } else {
        fail++;
      }
      
      if (session.symptome_description === 'Other') {
        otherCount++;
      }
    });

    const passRate = total > 0 ? ((pass / total) * 100).toFixed(1) : 0;

    return {
      total,
      pass,
      fail,
      passRate,
      otherCount
    };
  };

  const stats = calculateStats();

  return (
    <div className="historique-container">
      <div className="header">
        <h1>Recherche d'Historique par SN</h1>
        <p>Trouvez toutes les données d'un contrôleur DS5</p>
      </div>

      <div className="search-box">
        <div className="search-input">
          <input
            type="text"
            value={sn}
            onChange={(e) => setSn(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Entrez le numéro de série..."
            disabled={loading}
          />
          <button onClick={searchAll} disabled={loading || !sn.trim()}>
            {loading ? (
              <span className="spinner"></span>
            ) : (
              '🔍 Rechercher'
            )}
          </button>
        </div>
        {error && <div className="error">{error}</div>}
      </div>

      {results && (
        <div className="results-container">
          <div className="results-header">
            <h2>Résultats pour: <span className="sn-highlight">{sn}</span></h2>
            
            <div className="stats-summary">
              <div className="stat-item">
                <span className="stat-label">Total sessions</span>
                <span className="stat-value">{stats.total}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">PASS</span>
                <span className="stat-value pass">{stats.pass}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">FAIL</span>
                <span className="stat-value fail">{stats.fail}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Taux PASS</span>
                <span className="stat-value">{stats.passRate}%</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Sessions "Other"</span>
                <span className="stat-value other">{stats.otherCount}</span>
              </div>
            </div>

            <div className="counts">
              <span>Sessions: {results.sessions.length}</span>
              <span>Quick: {results.quick.length}</span>
              <span>Historique: {results.history.length}</span>
            </div>
          </div>

          <div className="tabs">
            <button 
              className={activeTab === 'sessions' ? 'active' : ''}
              onClick={() => setActiveTab('sessions')}
            >
              Sessions ({results.sessions.length})
            </button>
            <button 
              className={activeTab === 'quick' ? 'active' : ''}
              onClick={() => setActiveTab('quick')}
            >
              Quick Drift ({results.quick.length})
            </button>
            <button 
              className={activeTab === 'history' ? 'active' : ''}
              onClick={() => setActiveTab('history')}
            >
              Historique DS5 ({results.history.length})
            </button>
          </div>

          <div className="results-content">
            {activeTab === 'sessions' && renderTable(results.sessions, 'sessions')}
            {activeTab === 'quick' && renderTable(results.quick, 'quick')}
            {activeTab === 'history' && renderTable(results.history, 'history')}
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoriqueSN;